import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Flex, { FlexDirection } from "@galaxy-io/dls/layout/Flex";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import Widget from "@galaxy-io/dls/widget/Widget";
import type { SavedQuery } from "@gtm/contracts";
import {
  BookmarkSimpleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { apiClient, unwrap } from "@/lib/api-client";

export function SavedQueriesDrawer({
  open,
  onClose,
  activeId,
  onActiveDeleted,
}: {
  open: boolean;
  onClose: () => void;
  activeId: string | null;
  onActiveDeleted: () => void;
}) {
  const router = useRouter();
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<SavedQuery | null>(null);
  const saved = useQuery({
    queryKey: ["saved-queries"],
    enabled: open,
    queryFn: () => unwrap(apiClient().GET("/console/queries", {})),
  });
  const matches =
    saved.data?.filter((item) =>
      `${item.name}\n${item.query}`.toLowerCase().includes(search.trim().toLowerCase()),
    ) ?? [];
  return (
    <SurfaceDialog
      drawer
      title="Saved queries"
      headerIcon={BookmarkSimpleIcon}
      headerDescription="Find and reuse your workspace’s SQL."
      open={open}
      onClose={onClose}
      headerActions={
        <Button
          label="New query"
          icon={PlusIcon}
          size={ButtonSize.SMALL}
          variant={ButtonVariant.SECONDARY}
          onClick={() => {
            onClose();
            router.push(`/query?new=${crypto.randomUUID()}`);
          }}
        />
      }
    >
      <TextInput
        aria-label="Search saved queries"
        placeholder="Search names or SQL…"
        icon={MagnifyingGlassIcon}
        value={search}
        onChange={setSearch}
        fillWidth
      />
      {saved.isPending || saved.error ? (
        <QueryState
          error={saved.error}
          loadingMessage="Loading saved queries…"
          onRetry={() => void saved.refetch()}
        />
      ) : !saved.data?.length ? (
        <EmptyState
          header="No saved queries yet"
          description="Save a query from the console to keep it here for next time."
        />
      ) : !matches.length ? (
        <EmptyState
          header="No matching queries"
          description="Try another name, table or SQL keyword."
        />
      ) : (
        <Flex direction={FlexDirection.COLUMN} gap={12}>
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            {matches.length} {matches.length === 1 ? "query" : "queries"}
          </Text>
          {matches.map((item) => (
            <Widget
              key={item.id}
              header={item.name}
              subheader={item.id === activeId ? "Open in console" : undefined}
              isInteractive
              isSelected={item.id === activeId}
              ariaLabel={`Open ${item.name}`}
              onClick={() => {
                onClose();
                router.push(`/query?id=${encodeURIComponent(item.id)}`);
              }}
              actions={
                <Button
                  ariaLabel={`Delete ${item.name}`}
                  tooltip={`Delete ${item.name}`}
                  icon={TrashIcon}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  onClick={() => setPending(item)}
                />
              }
            >
              <Text
                family={FontFamily.MONO}
                size={TextSize.BODY_SM}
                variant={TextVariant.SECONDARY}
                lineClamp={3}
              >
                {item.query}
              </Text>
            </Widget>
          ))}
        </Flex>
      )}
      <ConfirmAction
        isOpen={!!pending}
        header="Delete saved query"
        description={<>Delete “{pending?.name}”? This removes it from the workspace library.</>}
        label="Delete query"
        isDestructive
        onOpenChange={(next) => {
          if (!next) setPending(null);
        }}
        onConfirm={async () => {
          if (!pending) return;
          await unwrap(
            apiClient().DELETE("/console/queries/{id}", {
              params: { path: { id: pending.id } },
              body: { revision: pending.revision },
            }),
          );
          client.setQueryData<SavedQuery[]>(["saved-queries"], (items) =>
            items?.filter((item) => item.id !== pending.id),
          );
          if (pending.id === activeId) onActiveDeleted();
          setPending(null);
          void client.invalidateQueries({ queryKey: ["saved-queries"] });
        }}
      />
    </SurfaceDialog>
  );
}
